import { DELIVERY_IN_PICKUP_REGION_ORDER_MINIMUM, FREE_DELIVERY_MINIMUM_THRESHOLD } from 'common/Constants';
import { formatCurrency } from 'common/FormatUtilities';
import { isFulfillmentOptionDelivery } from 'common/FulfillmentOptionUtils';
import React, { useMemo } from 'react';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { useBulkDiscount } from 'src/shared/hooks/useBulkDiscount';
import { useSelector } from 'src/store';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import styled from 'styled-components';
import { Colors } from 'web-common/src/shared/Constants';
import {
  BorderRadius,
  FontFamily,
  FontSize,
  FontWeight,
  LineHeight,
  Mixins,
  Spacing,
} from 'web-common/src/shared/styles';

const DeliveryBanner = styled.div`
  ${Mixins.Flex.center('column')}
  text-align: center;

  background-color: ${Colors.MINT_100};
  padding: ${Spacing.SINGLE} ${Spacing.HALF};
  border-radius: ${BorderRadius.S};

  font-family: ${FontFamily.SANS};
  color: ${Colors.BLACK};
  line-height: ${LineHeight.XXS};
  font-size: ${FontSize.XS};
  font-weight: ${FontWeight.NORMAL};

  p {
    display: inline;
  }
`;

const DeliveryBannerWarning = styled.div`
  ${Mixins.Flex.center('column')}
  text-align: center;

  background-color: ${Colors.BELL_PEPPER_LITE_30};
  padding: ${Spacing.HALF};
  border-radius: ${BorderRadius.S};

  font-family: ${FontFamily.SANS};
  color: ${Colors.BELL_PEPPER_DARK_10};
  line-height: ${LineHeight.XXS};
  font-size: ${FontSize.XS};
  font-weight: ${FontWeight.NORMAL};

  p {
    display: inline;
  }
`;

const Heavy = styled.span`
  font-weight: ${FontWeight.SEMI_BOLD};
`;

interface IProps {
  subtotal: number;
  fulfillmentOption?: string;
}

export const ConsumerOrderCartDeliveryBanner: React.FC<IProps> = ({ subtotal, fulfillmentOption }) => {
  const [, , zipCodeMetaData] = useCachedZipCode();
  const { code } = usePromotionalsHook();
  const { promoApplied } = useBulkDiscount();

  const { minimumDiscountedShippingAmount, discountedDeliveryFee } = zipCodeMetaData;

  const isMultiCart = useSelector((state) => state.cart.isMultiCart);

  const minimumDirectDeliveryOrder = isFulfillmentOptionDelivery(fulfillmentOption ?? null)
    ? DELIVERY_IN_PICKUP_REGION_ORDER_MINIMUM
    : null;

  // If the promo code is only for meal plans, we can't use it in the cart for explore so we don't apply it to the subtotal
  const subtotalWithPromoApplied = useMemo(
    () => (code?.isMealPlanExclusive ? subtotal : subtotal - promoApplied),
    [code?.isMealPlanExclusive, subtotal, promoApplied]
  );
  const formattedDiscount = useMemo(
    () => (discountedDeliveryFee === 0 ? 'free' : formatCurrency(discountedDeliveryFee)),
    [discountedDeliveryFee]
  );
  const amountUntilDiscount = useMemo(
    () =>
      formatCurrency((minimumDiscountedShippingAmount ?? FREE_DELIVERY_MINIMUM_THRESHOLD) - subtotalWithPromoApplied),
    [minimumDiscountedShippingAmount, subtotalWithPromoApplied]
  );
  const hasFreeDeliveryPromo = useMemo(
    () => code?.isFreeDelivery && !code.isMealPlanExclusive,
    [code?.isFreeDelivery, code?.isMealPlanExclusive]
  );

  if (minimumDirectDeliveryOrder && subtotal < minimumDirectDeliveryOrder) {
    return (
      <DeliveryBannerWarning>
        <span>
          Order amount must be at least <Heavy>{formatCurrency(DELIVERY_IN_PICKUP_REGION_ORDER_MINIMUM, true)}</Heavy>.
        </span>
      </DeliveryBannerWarning>
    );
  }
  // Possible if we have a zipcode overridding delivery fee (thus we dont provide free delivery)
  if (!minimumDiscountedShippingAmount || minimumDiscountedShippingAmount === Number.POSITIVE_INFINITY) {
    return null;
  }

  return subtotalWithPromoApplied < minimumDiscountedShippingAmount && !hasFreeDeliveryPromo ? (
    <DeliveryBanner>
      {isMultiCart ? (
        <span>
          You're <span className='font-semibold'>{amountUntilDiscount}</span> away from {formattedDiscount} delivery!
        </span>
      ) : (
        <span>
          Add&nbsp;
          <Heavy>{amountUntilDiscount}&nbsp;</Heavy>to your order for {formattedDiscount} delivery.
        </span>
      )}
    </DeliveryBanner>
  ) : (
    <DeliveryBanner>
      <span>
        You qualified for&nbsp;
        <Heavy>{formattedDiscount} delivery!</Heavy>
      </span>
    </DeliveryBanner>
  );
};
